import getConfig from 'next/config';
import { queryClientBigLogosView } from 'remote-com-shared/components/ClientLogos/services';
import { getForm } from 'remote-com-shared/domains/hubspot/services';
import { getDocumentMeta } from 'remote-com-shared/domains/navigation/hrefResolver';

import { SinglePageScrollLayout } from '@/layouts/SinglePageScrollLayout';
import { queryClientTestimonialsView } from '@/src/components/Testimonials/services';
import { queryPricingOverview } from '@/src/domains/pricing/services';
import { querySharedServices } from '@/src/domains/shared/services';
import {
  queryGetAllPromoCodesDetails,
  queryGetStartedView,
  queryGetAllPromoCodeDocuments,
  queryAllPaidMarketingCampaigns,
  queryPaidMarketingCampaignView,
} from '@/src/views/GetStarted/services';
import { queryGlobalPayrollOverview } from '@/src/views/Platform/services';
import { SlugDistribution } from '@/src/views/SlugDistribution';

export { SlugDistribution as default } from '@/src/views/SlugDistribution';

SlugDistribution.Layout = SlugDistribution.Layout || SinglePageScrollLayout;

const { publicRuntimeConfig } = getConfig();
const twoStepGetStartedFormId = publicRuntimeConfig.HUBSPOT_TWO_STEP_GET_STARTED_FORM_ID;

const hubspotFormDefinition = {};
const twoStepGetStartedHubspotFormDefinition = {};
let paidMarketingCampaignPages = null;
let paidMarketingCampaignSlugs = null;

export async function getStaticPaths() {
  const promoCodePages = await queryGetAllPromoCodeDocuments();
  if (paidMarketingCampaignPages === null) {
    paidMarketingCampaignPages = await queryAllPaidMarketingCampaigns();
    paidMarketingCampaignSlugs = paidMarketingCampaignPages.map((campaignDocument) => {
      const { uid, lang } = getDocumentMeta(campaignDocument);

      return {
        slug: uid,
        locale: lang,
      };
    });
  }

  const localePaths = promoCodePages?.reduce((pathList, currentDocument) => {
    // Each doc for each supported locale
    const currentPathList = currentDocument.map((document) => {
      const { uid, lang } = getDocumentMeta(document);

      return {
        params: { slug: uid },
        locale: lang,
      };
    });

    return pathList.concat(currentPathList);
  }, []);

  const paidMarketingCampaignPaths = paidMarketingCampaignPages.map((campaignDocument) => {
    const { uid, lang } = getDocumentMeta(campaignDocument);

    return {
      params: { slug: uid },
      locale: lang,
    };
  });

  const allLocalePaths = [...localePaths, ...paidMarketingCampaignPaths];

  return {
    paths: allLocalePaths,
    fallback: false,
  };
}

export async function getStaticProps({ preview = false, previewData = {}, locale, params }) {
  const { slug } = params;
  const sharedServices = await querySharedServices({ previewData, locale });

  const clientLogosView = await queryClientBigLogosView({ previewData, locale });
  const clientTestimonialsView = await queryClientTestimonialsView({ previewData, locale });
  const getStartedView = await queryGetStartedView({ previewData, locale });
  const pricingOverview = await queryPricingOverview({ previewData, locale });

  const twoStepFormId = sharedServices.localisedContent.twoStepFormId || twoStepGetStartedFormId;

  if (!hubspotFormDefinition[locale]) {
    const { form_id: formId } = await queryGlobalPayrollOverview({ previewData, locale });
    const { data: hubspotFormData } = await getForm(formId);
    hubspotFormDefinition[locale] = hubspotFormData;
  }

  if (!twoStepGetStartedHubspotFormDefinition[locale]) {
    const { data: hubspotFormData } = await getForm(twoStepFormId);
    twoStepGetStartedHubspotFormDefinition[locale] = hubspotFormData;
  }

  if (!getStartedView) {
    return {
      notFound: true,
    };
  }

  // Check if the page is of type Paid_marketing_campaign_LP
  if (paidMarketingCampaignPages === null) {
    paidMarketingCampaignPages = await queryAllPaidMarketingCampaigns();
    paidMarketingCampaignSlugs = paidMarketingCampaignPages.map((campaignDocument) => {
      const { uid, lang } = getDocumentMeta(campaignDocument);

      return {
        slug: uid,
        locale: lang,
      };
    });
  }
  if (
    paidMarketingCampaignSlugs.find(
      (pathProps) => pathProps.slug === slug && pathProps.locale === locale
    )
  ) {
    const paidMarketingView = await queryPaidMarketingCampaignView({
      uid: slug,
      previewData,
      locale,
    });
    const { get_started_form_id: paidMarketingCampaignFormId } = paidMarketingView;
    const { data: paidMarketingCampaignFormDefinition } = await getForm(
      paidMarketingCampaignFormId
    );

    return {
      props: {
        preview,
        slug,
        clientLogosView,
        clientTestimonialsView,
        pricingOverview,
        getStartedView: paidMarketingView,
        hubspotFormDefinition,
        twoStepGetStartedHubspotFormDefinition: paidMarketingCampaignFormDefinition,
        sharedServices,
      },
    };
  }

  const allPromoCodePageDetailView = await queryGetAllPromoCodesDetails({
    previewData,
    locale,
  });
  const promoCodePageDetailView = allPromoCodePageDetailView.find(
    (view) => view.landing_url.slice(1) === slug
  );

  // Promo code page does not exist
  if (!promoCodePageDetailView) {
    return {
      notFound: true,
    };
  }

  const seoMeta = getDocumentMeta(promoCodePageDetailView);

  return {
    props: {
      preview,
      slug,
      clientLogosView,
      clientTestimonialsView,
      pricingOverview,
      getStartedView,
      promoCodeLandingPage: true,
      promoCodePageDetailView,
      hubspotFormDefinition: hubspotFormDefinition[locale],
      twoStepGetStartedHubspotFormDefinition: twoStepGetStartedHubspotFormDefinition[locale],
      sharedServices,
      seoMeta,
    },
  };
}
